import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import AsyncCreatableSelect, {
  type AsyncCreatableProps
} from "react-select/async-creatable";
import type {
  GroupBase,
  ControlProps,
  InputProps,
  MenuProps,
  ActionMeta,
  OnChangeValue,
  InputActionMeta,
  FilterOptionOption
} from "react-select";
import { components, createFilter } from "react-select";
import { isTacticAppWebView } from "@gettactic/helpers/src/isTacticAppWebView";
import { MobileDrawer } from "@v2/mobile/MobileDrawer/MobileDrawer";

type AnyOpt = { label: string; value: string; keywords?: string };

type AriaProp = {
  "aria-label"?: string;
};

export type CustomAsyncSelectProps<
  Option extends AnyOpt = AnyOpt,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = AsyncCreatableProps<Option, IsMulti, Group> &
  AriaProp & {
    drawerSearchPlaceholder?: string;
  };

function CustomAsyncSelectDesktop<
  Option extends AnyOpt,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
>(props: CustomAsyncSelectProps<Option, IsMulti, Group>) {
  const {
    components: userComponents,
    styles: userStyles,
    isSearchable,
    onChange,
    ...rest
  } = props;

  return (
    <AsyncCreatableSelect<Option, IsMulti, Group>
      {...rest}
      components={userComponents}
      styles={userStyles}
      menuPosition="fixed"
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      closeMenuOnScroll={false}
      isSearchable={isSearchable ?? true}
      onChange={onChange}
    />
  );
}

function CustomAsyncSelectMobile<
  Option extends AnyOpt,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
>(props: CustomAsyncSelectProps<Option, IsMulti, Group>) {
  const {
    components: userComponents,
    styles: userStyles,
    drawerSearchPlaceholder,
    filterOption: userFilterOption,
    onInputChange,
    onChange,
    "aria-label": ariaLabel,
    isDisabled,
    ...rest
  } = props;

  const [menuOpen, setMenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [menuContainer, setMenuContainer] = useState<HTMLDivElement | null>(
    null
  );

  const refocusSearch = useCallback(() => {
    requestAnimationFrame(() => searchRef.current?.focus());
  }, []);

  const handleControlPress = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setMenuOpen(true);
      refocusSearch();
    },
    [refocusSearch]
  );

  const handleDrawerInputChange = useCallback(
    (val: string) => {
      setInputValue(val);
      const meta: InputActionMeta = {
        action: "input-change",
        prevInputValue: inputValue
      };
      onInputChange?.(val, meta);
      return val;
    },
    [inputValue, onInputChange]
  );

  const handleChange = useCallback(
    (value: OnChangeValue<Option, IsMulti>, meta: ActionMeta<Option>) => {
      onChange?.(value, meta);
      if (meta.action === "select-option" || meta.action === "create-option") {
        setMenuOpen(false);
      }
    },
    [onChange]
  );

  function NoFocusInput(p: InputProps<Option, IsMulti, Group>) {
    return (
      <components.Input
        {...p}
        readOnly
        onFocus={(e) => {
          e.preventDefault();
          e.stopPropagation();
          refocusSearch();
        }}
        tabIndex={-1}
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none"
        }}
      />
    );
  }

  function PortalMenu(p: MenuProps<Option, IsMulti, Group>) {
    const { children, innerProps } = p;
    if (!menuContainer) return null;
    return ReactDOM.createPortal(
      <div
        {...innerProps}
        onMouseDown={(e) => e.preventDefault()}
        onTouchStart={(e) => e.preventDefault()}
      >
        {children}
      </div>,
      menuContainer
    );
  }

  function DrawerControl(p: ControlProps<Option, IsMulti, Group>) {
    return (
      <div onMouseDown={handleControlPress} onTouchStart={handleControlPress}>
        <components.Control {...p}>{p.children}</components.Control>
      </div>
    );
  }

  const onSearchBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (menuOpen) {
        e.preventDefault();
        refocusSearch();
      }
    },
    [menuOpen, refocusSearch]
  );

  useEffect(() => {
    if (menuOpen) {
      const t = setTimeout(refocusSearch, 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [menuOpen, refocusSearch]);

  const filterOption =
    userFilterOption ??
    createFilter<Option>({
      ignoreAccents: true,
      ignoreCase: true,
      trim: true,
      stringify: (opt: FilterOptionOption<Option>) =>
        `${opt.label} ${(opt.data.keywords ?? "") as string}`
    });

  return (
    <>
      <MobileDrawer
        isOpen={menuOpen}
        onOpenChange={setMenuOpen}
        size="fit"
        scrollableContent
        ariaLabel={ariaLabel ?? "Options"}
        topContent={
          <div className="p-3">
            <input
              ref={searchRef}
              type="text"
              value={inputValue}
              onChange={(e) => handleDrawerInputChange(e.target.value)}
              onBlur={onSearchBlur}
              placeholder={drawerSearchPlaceholder || "Search…"}
              className="w-full rounded-lg border px-3 py-2 text-base outline-none dark:bg-neutral-800"
              inputMode="search"
              autoCorrect="off"
              autoCapitalize="none"
              enterKeyHint="search"
            />
          </div>
        }
      >
        <div
          className="max-h-[60vh] overflow-auto px-2 pb-3"
          ref={setMenuContainer}
        />
      </MobileDrawer>

      <AsyncCreatableSelect<Option, IsMulti, Group>
        {...(rest as Omit<
          CustomAsyncSelectProps<Option, IsMulti, Group>,
          "onInputChange" | "onChange" | "components" | "styles"
        >)}
        isSearchable
        inputValue={inputValue}
        onInputChange={(val, meta) =>
          meta.action === "input-change" ? handleDrawerInputChange(val) : val
        }
        filterOption={filterOption}
        menuIsOpen={isDisabled ? false : menuOpen}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        menuShouldScrollIntoView={false}
        captureMenuScroll={false}
        closeMenuOnScroll={false}
        blurInputOnSelect={false}
        openMenuOnFocus={false}
        closeMenuOnSelect
        menuPortalTarget={null}
        menuPosition="fixed"
        backspaceRemovesValue={false}
        tabSelectsValue={false}
        onChange={handleChange}
        components={{
          Input: NoFocusInput,
          Menu: PortalMenu,
          Control: DrawerControl,
          ...(userComponents || {})
        }}
        styles={{
          menu: (base) => ({ ...base, zIndex: 9999, marginTop: 10 }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          ...(userStyles || {})
        }}
      />
    </>
  );
}

export function CustomAsyncSelect<
  Option extends AnyOpt = AnyOpt,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(props: CustomAsyncSelectProps<Option, IsMulti, Group>) {
  const mobile = isTacticAppWebView();
  if (mobile) {
    return <CustomAsyncSelectMobile<Option, IsMulti, Group> {...props} />;
  }
  return <CustomAsyncSelectDesktop<Option, IsMulti, Group> {...props} />;
}
